<script lang="ts">
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 0.2.0
 * @copyright  Copyright (C) 2025-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *  2025-04-09 (1.0.0) (jhoward)
 *    - <section-item-subgroup> handles displaying a subgroup of dining menu items.
 *
 *  2025-04-09 (1.0.1) (jhoward)
 *    - Add <EasterFoodItem> to <section-item-subgroup>.
 *
 *  2025-05-05 (1.0.2) (jhoward)
 *    - Add <MothersDayFoodItem> to <section-item-subgroup>.
 *
 * 2026-02-17 (0.1.0) (jhoward)
 *   - Import and register App\Library\Js\Components\Menu\Item\Chi_chineseNewYearItem.vue as ChiChineseNewYearItem.
 *   - Enables rendering of Chinese New Year menu items within section subgroups.
 *
 * 2026-07-20 (0.2.0) (jhoward)
 *   - Import and register App\Library\Js\Components\Menu\Item\Chi_chineseNewYearItem.vue and MothersDay_cocktailItem.vue for subgroup rendering.
 */

import {defineComponent, PropType} from 'vue'
import {
  methodGetPricingTypes,
  methodShowBIN
} from "../../../helpers/menu-sections";
import {ALLOWED_ITEM_COMPONENTS, MENU_ITEM_GROUP_LAYOUT, MENU_ITEM_TAG_LOCATIONS, MenuItem, MenuSection} from "../defs";
import BiminiFoodItem from "../item/Bimini_foodItem.vue";
import BiminiSteakItem from "../item/Bimini_steakItem.vue";
import ChiDefaultItem from "../item/Chi_defaultItem.vue";
import ChiChineseNewYearItem from "../item/Chi_chineseNewYearItem.vue";
import ChiWineListItem from "../item/Chi_wineListItem.vue";
import MenuItemTextOnly from "../item/MenuItemTextOnly.vue";
import MenuItemHighlightedBox from "../item/MenuItemHighlightedBox.vue";
import BiminiCocktailItem from "../item/Bimini_cocktailItem.vue";
import HighLimitFoodItem from "../item/HighLimit_foodItem.vue";
import oceanoDefaultItem from "../item/OceanoDefaultItem.vue";
import PanchoWilliesFoodItem from "../item/PanchoWillies_foodItem.vue";
import PanchoWilliesDrinkItem from "../item/PanchoWillies_drinkItem.vue";
import PanchoWilliesCervezasItem from "../item/PanchoWillies_cervezasItem.vue";
import WvSteakHouseFoodItem from "../item/WvSteakHouseFoodItem.vue";
import WvSteakHouseSteakItem from "../item/WvSteakHouseSteakItem.vue";
import WvSteakHouseCocktailItem from "../item/WvSteakHouseCocktailItem.vue";
import WvSteakHouseSignatureDrinkItem from "../item/WvSteakHouseSignatureDrinkItem.vue";
import WvSteakHouseWineListItem from "../item/WvSteakHouseWineListItem.vue";
import BackBarDefaultItem from "../item/BackBarDefaultItem.vue";
import ThanksgivingFoodItem from "../item/Thanksgiving_foodItem.vue";
import NewYearsFoodItem from "../item/NewYears_foodItem.vue";
import ChristmasFoodItem from "../item/Christmas_foodItem.vue";
import ChristmasDrinkItem from "../item/Christmas_drinkItem.vue";
import ValentinesDayFoodItem from "../item/ValentinesDay_foodItem.vue";
import EasterFoodItem from "../item/Easter_foodItem.vue";
import MothersDayFoodItem from "../item/MothersDay_foodItem.vue";
import MothersDayCocktailItem from "../item/MothersDay_cocktailItem.vue";
import DefaultItem from "../item/defaultItem.vue";
import WineListItem from "../item/wineListItem.vue";
import MenuItemPrice from "../item/MenuItemPrice.vue";
import RomanzaDefaultItem from "../item/Romanza_defaultItem.vue";
import RomanzaDessertItem from "../item/Romanza_dessertItem.vue";

export default defineComponent({
  name: "SectionItemSubgroup",
  components: {
    MenuItemPrice,
    BiminiFoodItem,
    BiminiSteakItem,
    BiminiCocktailItem,
    ChiDefaultItem,
    ChiChineseNewYearItem,
    ChiWineListItem,
    MenuItemTextOnly,
    MenuItemHighlightedBox,
    HighLimitFoodItem,
    oceanoDefaultItem,
    PanchoWilliesFoodItem,
    PanchoWilliesDrinkItem,
    PanchoWilliesCervezasItem,
    WvSteakHouseFoodItem,
    WvSteakHouseSteakItem,
    WvSteakHouseCocktailItem,
    WvSteakHouseSignatureDrinkItem,
    WvSteakHouseWineListItem,
    BackBarDefaultItem,
    ThanksgivingFoodItem,
    NewYearsFoodItem,
    ChristmasFoodItem,
    ChristmasDrinkItem,
    ValentinesDayFoodItem,
    EasterFoodItem,
    MothersDayFoodItem,
    DefaultItem,
    WineListItem,
    RomanzaDefaultItem, RomanzaDessertItem,
  },
  props: {
    items: {
      type: Array as PropType<MenuItem[]>,
      required: true,
    },
    groupTitle: {
      type: String,
      required: true,
    },
    subgroupTitle: {
      type: String,
      required: true,
    },
    menuItemComponent: {
      type: String,
      required: true,
      validate: (value: string) => ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    layout: {
      type: String as PropType<MENU_ITEM_GROUP_LAYOUT>,
      default: ''
    },
    isSubgroupPricingShown: {
      type: Boolean,
      default: false
    },
    pricingTypes: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    isPageLinkGrid: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      ALLOWED_ITEM_COMPONENTS
    };
  },
  computed: {
    isBinShowing(): boolean {
      return this.showBIN(this.items, this.section);
    },
    subgroupClasses(): { [key: string]: boolean } {
      return {
        'menu--item_subgroup-show_BIN': this.isBinShowing,
        'menu--item_subgroup-show-pricing': this.isSubgroupPricingShown
      };
    },
  },
  methods: {
    getPricingTypes: methodGetPricingTypes,
    showBIN: methodShowBIN,

    getMenuItemComponentOverride(item: MenuItem, defaultValue?: string): string {
      if (item.mi_custom_flipbook_component.length) {
        return item.mi_custom_flipbook_component;
      } else if ('string' === typeof defaultValue && -1 !== this.ALLOWED_ITEM_COMPONENTS.indexOf(defaultValue)) {
        return defaultValue;
      }

      return this.menuItemComponent;
    },

    handleItemRows(rows: number) {
      this.$emit('rows', rows);
    },

    sectionContentClasses(section: MenuSection): { [key: string]: boolean } {
      const output: { [key: string]: boolean } = {};
      if (this.items) {
        output['item-count-' + this.items.length] = true;
      }
      return output;
    },
  },
})
</script>

<template>
  <div class="menu--item_subgroup"
       :class="subgroupClasses"
       :data-pricing-type-count="(section.ms_group_by_price) ? 1 : (pricingTypes.length || 1)">
    <template v-if="section.ms_group_by_price">
      <template v-for="pricingType in pricingTypes">
        <h3 class="menu--section_subgroup_title">
          <span>{{ subgroupTitle }}</span>
          <menu-item-price :price-to-display="pricingType" :price-label="items[0]?.price_label || ''"></menu-item-price>
        </h3>
        <component v-for="(item, itemIndex) in items" :is="getMenuItemComponentOverride(item, menuItemComponent)"
                   v-if="item.mi_price === pricingType"
                   :key="item.mi_id" :item="item" :pricing-types="[]"
                   :is-grouped-by-price="!!section.ms_group_by_price"
                   :item-index="itemIndex" @rows="handleItemRows"
                   :section="section"
                   :show-bin="isBinShowing" :menu-id="section.menu_id" :tag-location="menuItemTagLocation"></component>
      </template>
    </template>
    <template v-else>
      <template v-if="subgroupTitle.length || (section.ms_show_pricing_header && pricingTypes.length)">
        <template v-if="layout === 'title-above'">
          <h3 class="menu--section_subgroup_title menu--section_subgroup_title-title_above">
            <span>{{ subgroupTitle }}</span>
          </h3>
        </template>
        <h3 class="menu--section_subgroup_title">
          <span v-if="layout !== 'title-above'">{{ subgroupTitle }}</span>
          <span v-else></span>
          <span v-if="section.ms_show_pricing_header" v-for="pricingType in pricingTypes">{{ pricingType }}</span>
        </h3>
      </template>

      <div v-if="isPageLinkGrid" class="viewport" :class="sectionContentClasses(section)">
        <div class="body-container-scroller">
          <component v-for="(item, itemIndex) in items" :is="getMenuItemComponentOverride(item, menuItemComponent)"
                     :key="item.mi_id" :item="item" :pricing-types="pricingTypes"
                     :is-grouped-by-price="!!section.ms_group_by_price"
                     :item-index="itemIndex" @rows="handleItemRows"
                     :section="section"
                     :show-bin="isBinShowing" :menu-id="section.menu_id" :tag-location="menuItemTagLocation"></component>
        </div>
      </div>
      <template v-else>
        <component v-for="(item, itemIndex) in items" :is="getMenuItemComponentOverride(item, menuItemComponent)"
                   :key="item.mi_id" :item="item" :pricing-types="pricingTypes"
                   :is-grouped-by-price="!!section.ms_group_by_price"
                   :item-index="itemIndex" @rows="handleItemRows"
                   :section="section"
                   :show-bin="isBinShowing" :menu-id="section.menu_id" :tag-location="menuItemTagLocation"></component>
      </template>
    </template>
  </div>
</template>

<style scoped>
.menu--item_subgroup {
  display: grid;
  grid-template-columns: 100%;
  grid-gap: 1em;
}

.menu--item_subgroup + .menu--item_subgroup {
  margin-top: 1em;
}

/*START MULTI-COLUMN LAYOUTS*/
.menu--section-two_column .menu--item_subgroup {
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 5em;
}

.menu--section-two_column .menu--section_subgroup_title {
  grid-column: 1 / -1;
}

.menu--section-three_column .menu--item_subgroup {
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: 2em;
  grid-row-gap: 0;
}

.menu--section-three_column .menu--section_subgroup_title {
  grid-column: span 3;
}

@media (max-width: 400px) {
  .menu--section-three_column .menu--item_subgroup {
    grid-template-columns: 1fr;
    grid-column-gap: 0;
  }
}

@media (min-width: 401px) and (max-width: 700px) {
  .menu--section-three_column .menu--item_subgroup {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 700px) {
  .menu--section-two_column .menu--item_subgroup {
    grid-template-columns: 1fr;
    grid-column-gap: 0;
  }
}
/*END MULTI-COLUMN LAYOUTS*/

.menu--section_title .menu--item_subgroup {
  grid-gap: 0;
  position: relative;
}

.visual_menu--container .menu--item_subgroup:first-of-type .menu--section_subgroup_title:first-of-type {
  margin-top: -0.8em;
}

.menu--section_subgroup_title {
  font-weight: 600;
  font-size: 1em;
  grid-gap: 0.25em;
  color: #3e2603;
  margin: 0 auto 0.5em auto;
  padding: 0;
  text-align: center;
}

@media (prefers-contrast: more) {
  .menu--section_subgroup_title {
    color: #000;
  }
}

.menu--section-show_pricing_header .menu--section_subgroup_title {
  text-align: initial;
}

.menu--section-show_pricing_header .menu--section_subgroup_title span:nth-child(n + 2) {
  text-align: right;
}

.menu--section_subgroup_title.menu--section_subgroup_title-title_above {
  display: block;
  text-align: center;
  margin-top: 0;
  margin-bottom: 0;
}

.menu--section_subgroup_title.menu--section_subgroup_title-title_above + .menu--section_subgroup_title {
  margin-top: 0 !important;
}

.menu--section-three_column .menu--section_subgroup_title {
  grid-column: 1 / -1;
}


.menu--section-show_bin .menu--section_subgroup_title,
.menu--section-show_pricing_header .menu--section_subgroup_title {
  font-size: 1em;
  width: 100%;
}


.menu--section-show_pricing_header .menu--section_subgroup_title span:first-child {
  font-size: 1.2em;
  font-weight: bold;
}

.menu--section-show_bin .menu--item_subgroup-show_BIN .menu--section_subgroup_title span:first-child,
.menu--section-show_pricing_header .menu--item_subgroup-show_BIN .menu--section_subgroup_title span:first-child {
  grid-column: span 2;
}

.menu--section-show_bin .menu--item_subgroup:not(:first-child) .menu--section_subgroup_title,
.menu--section-show_pricing_header .menu--item_subgroup:not(:first-child) .menu--section_subgroup_title {
  margin-top: 1em;
}

.menu--section-show_bin .menu--section_group_title + .menu--item_subgroup .menu--section_subgroup_title,
.menu--section-show_pricing_header .menu--section_group_title + .menu--item_subgroup .menu--section_subgroup_title {
  margin-top: 0;
}

.menu--section-show_bin .menu--item_subgroup:deep(.menu--item_title),
.menu--section-show_pricing_header .menu--item_subgroup:deep(.menu--item_title) {
  /*font-size: 0.9em;*/
  font-weight: normal;
}

.menu--section-show_bin .menu--section_subgroup_title span:not(:first-child),
.menu--section-show_pricing_header .menu--section_subgroup_title span:not(:first-child) {
  font-size: 0.9em;
}

.menu--section-group_by_price .menu--section_subgroup_title span:not(:first-child) {
  font-size: 0.6em;
}

.menu--section-show_bin .menu--item_subgroup .menu--section_subgroup_title span:nth-child(n + 2),
.menu--section-show_pricing_header .menu--item_subgroup .menu--section_subgroup_title span:nth-child(n + 2) {
  display: none;
}

.menu--section-show_bin .menu--item_subgroup-show-pricing .menu--section_subgroup_title span:nth-child(n + 2),
.menu--section-show_pricing_header .menu--item_subgroup-show-pricing .menu--section_subgroup_title span:nth-child(n + 2) {
  display: initial;
}



.menu--item_subgroup {
  --grid-col-width-price: 0.2fr;
  --grid-col-width-title: 2fr;
  --grid-col-width-bin: 0.2fr;
  --grid-col-count-price: 1;
}

.menu--item_subgroup[data-pricing-type-count="2"] {
  --grid-col-count-price: 2;
}

.menu--item_subgroup[data-pricing-type-count="3"] {
  --grid-col-count-price: 3;
}

.menu--item_subgroup[data-pricing-type-count="4"] {
  --grid-col-count-price: 4;
}

.menu--item_subgroup[data-pricing-type-count="5"] {
  --grid-col-count-price: 5;
}

.menu--section-long_price .menu--item_subgroup {
  --grid-col-width-price: 0.4fr;
}

.menu--section-group_by_price .menu--item_subgroup,
.menu--item_subgroup:deep(.menu--item-price_inline) {
  --grid-col-count-price: 0;
}

.menu--section-group_by_price .menu--item_subgroup:deep(.menu_item--price) {
  display: none;
}

.menu--item_subgroup:deep(.menu--item) {
  display: grid;
  grid-gap: 0.25em;
  grid-template-columns: var(--grid-col-width-title) repeat(var(--grid-col-count-price), minmax(max-content, var(--grid-col-width-price)));
}

.menu--section-show_pricing_header .menu--section_subgroup_title {
  display: grid;
  grid-gap: 0.25em;
  grid-template-columns: var(--grid-col-width-title) repeat(var(--grid-col-count-price), minmax(max-content, var(--grid-col-width-price)));
}

.menu--section-group_by_price .menu--section_subgroup_title {
  grid-template-columns: var(--grid-col-width-title) minmax(max-content, var(--grid-col-width-price));
}

.menu--item_subgroup-show_BIN:deep(.menu--item) {
  grid-template-columns: minmax(max-content, var(--grid-col-width-bin)) var(--grid-col-width-title) repeat(var(--grid-col-count-price), minmax(max-content, var(--grid-col-width-price)));
}

.menu--item_subgroup-show_BIN .menu--section_subgroup_title {
  grid-template-columns: var(--grid-col-width-bin) var(--grid-col-width-title) repeat(var(--grid-col-count-price), minmax(max-content, var(--grid-col-width-price)));
}

.menu--section-group_by_price .menu--item_subgroup-show_BIN .menu--section_subgroup_title {
  grid-template-columns: minmax(max-content, var(--grid-col-width-bin)) var(--grid-col-width-title) minmax(max-content, var(--grid-col-width-price));
}

.menu--item_subgroup-show_BIN {
  --grid-col-width-bin: 0.15fr;
}

.menu--item_subgroup[data-pricing-type-count="1"] {
  --grid-col-width-price: 0.3fr;
}

.menu--item_subgroup-show_BIN[data-pricing-type-count="1"] {
  --grid-col-width-price: 0.4fr;
}

.menu--item_subgroup[data-pricing-type-count="2"] {
  --grid-col-width-price: 0.3fr;
}

.menu--item_subgroup-show_BIN[data-pricing-type-count="2"] {
  --grid-col-width-price: 0.4fr;
}

.menu--item_subgroup[data-pricing-type-count="3"] {
  --grid-col-width-price: 0.3fr;
}

.menu--item_subgroup[data-pricing-type-count="4"] {
  --grid-col-width-price: 0.4fr;
}

.menu--item_subgroup[data-pricing-type-count="5"] {
  --grid-col-width-price: 0.6fr;
}



.menu--item_subgroup:deep(.menu--item.menu--item-multi_line_price > .menu--item_title),
.menu--item_subgroup:deep(.menu--item.menu--item-multi_line_price > .menu--item_price) {
  display: none;
}

.menu--item_subgroup:deep(.menu--item.menu--item-multi_line_price .menu--item_title) {
  font-size: 1.125em;
}

.menu--item_subgroup:deep(.menu--item.menu--item-multi_line_price .menu--item_description .menu--item_description) {
  font-size: 1em;
}

.menu--item_subgroup:deep(.menu--item.menu--item-multi_line_price .menu--item_description tr) {
  background-color: initial;
  border-bottom: initial;
}

.menu--item_subgroup:deep(.menu--item.menu--item-multi_line_price .menu--item_description td) {
  padding: 0.125em 0;
}
</style>